import React, { useEffect, useState, useCallback } from "react";
import { Trash2 } from "lucide-react";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import {
  getTalentPoolGlobal, scoreCandidateForJob, addToJobPipeline,
  getJobs, downloadFormattedCV, generateOutreachEmail, sendOutreachEmail,
  deleteCandidate,
} from "../api";
import { useAuth } from "../context/AuthContext";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const REC_COLORS: Record<string, { bg: string; fg: string }> = {
  "Strong Yes": { bg: "#dcfce7", fg: "#15803d" },
  "Yes":        { bg: "#d1fae5", fg: "#059669" },
  "Maybe":      { bg: "#fef3c7", fg: "#b45309" },
  "No":         { bg: "#fee2e2", fg: "#b91c1c" },
};

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  new:         { bg: "#e0f2fe", fg: "#0369a1" },
  reviewed:    { bg: "#fef3c7", fg: "#b45309" },
  shortlisted: { bg: "#ede9fe", fg: "#6d28d9" },
  hired:       { bg: "#dcfce7", fg: "#15803d" },
  rejected:    { bg: "#fee2e2", fg: "#b91c1c" },
};

function Chip({ label, colors }: { label: string; colors: { bg: string; fg: string } }) {
  return (
    <span style={{ background: colors.bg, color: colors.fg, padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
      {label}
    </span>
  );
}

export default function TalentPoolPage() {
  const { toast } = useToast();
  const confirm = useConfirm();
  const { downloading, download } = useBlockingDownload();
  const { user } = useAuth();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 480;
  const canDelete = ["admin", "admin_assistant", "hr", "recruiter"].includes(user?.role || "");

  const [candidates, setCandidates] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<Record<number, boolean>>({});

  // Filters
  const [q, setQ] = useState("");
  const [rec, setRec] = useState("");
  const [minExp, setMinExp] = useState("");
  const [maxExp, setMaxExp] = useState("");
  const [hasCV, setHasCV] = useState(false);

  // Per-card state
  const [scoring, setScoring] = useState<Record<number, boolean>>({});
  const [adding, setAdding] = useState<Record<number, boolean>>({});
  const [expanded, setExpanded] = useState<number | null>(null);

  // Add to job modal
  const [addModal, setAddModal] = useState<{ candidate: any } | null>(null);
  const [selectedJob, setSelectedJob] = useState("");

  // Score modal
  const [scoreModal, setScoreModal] = useState<{ candidate: any } | null>(null);
  const [scoreJob, setScoreJob] = useState("");

  // Outreach modal
  const [outreachCandidate, setOutreachCandidate] = useState<any | null>(null);
  const [outreachJobId, setOutreachJobId] = useState("");
  const [outreachDraft, setOutreachDraft] = useState<any | null>(null);
  const [outreachSending, setOutreachSending] = useState(false);
  const [generatingOutreach, setGeneratingOutreach] = useState(false);

  useEffect(() => {
    getJobs().then(setJobs).catch(() => {});
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (q.trim()) params.q = q.trim();
      if (rec) params.recommendation = rec;
      if (minExp) params.min_exp = parseFloat(minExp);
      if (maxExp) params.max_exp = parseFloat(maxExp);
      if (hasCV) params.has_cv = true;
      setCandidates(await getTalentPoolGlobal(params));
    } catch { toast("Failed to load talent pool", "error"); }
    finally { setLoading(false); }
  }, [q, rec, minExp, maxExp, hasCV]);

  useEffect(() => { search(); }, []);

  async function handleAddToJob() {
    if (!addModal || !selectedJob) return;
    const c = addModal.candidate;
    setAdding(a => ({ ...a, [c.candidate_id]: true }));
    try {
      await addToJobPipeline(parseInt(selectedJob), c.candidate_id);
      toast(`${c.name} added to pipeline`, "success");
      setAddModal(null); setSelectedJob("");
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to add", "error");
    } finally { setAdding(a => ({ ...a, [c.candidate_id]: false })); }
  }

  async function handleScore() {
    if (!scoreModal || !scoreJob) return;
    const c = scoreModal.candidate;
    setScoring(s => ({ ...s, [c.candidate_id]: true }));
    try {
      await scoreCandidateForJob(c.candidate_id, parseInt(scoreJob));
      toast(`Scoring ${c.name}… refresh in ~20 seconds`, "success");
      setScoreModal(null); setScoreJob("");
    } catch (e: any) {
      toast(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Scoring failed", "error");
    } finally { setScoring(s => ({ ...s, [c.candidate_id]: false })); }
  }

  async function handleDownloadCV(c: any) {
    await download(() => downloadFormattedCV(c.candidate_id), "CV download failed");
  }

  async function openOutreach(c: any) {
    setOutreachCandidate(c);
    setOutreachJobId(c.best_job_id ? String(c.best_job_id) : "");
    setOutreachDraft(null);
    setGeneratingOutreach(true);
    try {
      const draft = await generateOutreachEmail(c.candidate_id, c.best_job_id || undefined);
      setOutreachDraft(draft);
    } catch { toast("Failed to generate email draft", "error"); }
    finally { setGeneratingOutreach(false); }
  }

  async function handleSendOutreach() {
    if (!outreachCandidate || !outreachDraft) return;
    setOutreachSending(true);
    try {
      await sendOutreachEmail(outreachCandidate.candidate_id, {
        subject: outreachDraft.subject,
        body: outreachDraft.body,
        to_email: outreachCandidate.email,
        job_id: outreachJobId ? parseInt(outreachJobId) : undefined,
      });
      toast("Email sent!", "success");
      setOutreachCandidate(null); setOutreachDraft(null);
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to send", "error");
    } finally { setOutreachSending(false); }
  }

  async function handleDelete(c: any) {
    const ok = await confirm(`Delete ${c.name} from the database? This cannot be undone.`, { confirmLabel: "Delete", danger: true });
    if (!ok) return;
    setDeleting(d => ({ ...d, [c.candidate_id]: true }));
    try {
      await deleteCandidate(c.candidate_id);
      setCandidates(prev => prev.filter(x => x.candidate_id !== c.candidate_id));
      toast(`${c.name} deleted`, "success");
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Delete failed", "error");
    } finally { setDeleting(d => ({ ...d, [c.candidate_id]: false })); }
  }

  const activeJobs = jobs.filter((j: any) => j.status === "active");

  return (
    <div>
      {/* Width and padding come from ShellMain. Talent Pool is a candidate
          list like Candidates, Jobs and Offers — all of which run the full
          content width — so capping it here left it visibly narrower than
          its siblings, and its own padding stacked on the shell's. */}
      <LoadingOverlay active={downloading} />
      {/* The house `.page-header` block. A hand-rolled navy <h2> here read as a
          different kind of heading from the black <h1> every sibling page uses. */}
      <div className="page-header">
        <div>
          <h1>Global Talent Pool</h1>
          <p>
            Search all past candidates — existing AI scores shown instantly, doesn't count against your CVs processed quota
          </p>
        </div>
      </div>

      {/* ── Search & filters ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === "Enter" && search()}
          placeholder="Search name, email, role, location…"
          style={{ flex: "1 1 200px", minWidth: 0, padding: "9px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14 }}
        />
        <select value={rec} onChange={e => setRec(e.target.value)}
          style={{ flex: "1 1 140px", minWidth: 0, padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }}>
          <option value="">All recommendations</option>
          {["Strong Yes", "Yes", "Maybe", "No"].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input value={minExp} onChange={e => setMinExp(e.target.value)} placeholder="Min yrs"
          type="number" min={0} style={{ flex: "1 1 80px", minWidth: 0, padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
        <input value={maxExp} onChange={e => setMaxExp(e.target.value)} placeholder="Max yrs"
          type="number" min={0} style={{ flex: "1 1 80px", minWidth: 0, padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={hasCV} onChange={e => setHasCV(e.target.checked)} />
          Has CV
        </label>
        <AsyncButton onClick={search}
          style={{ flex: isMobile ? "1 1 100%" : "0 0 auto", background: NAVY, color: "white", border: "none", padding: "9px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
          Search
        </AsyncButton>
      </div>

      {/* ── Results ── */}
      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : candidates.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8", background: "#f8fafc", borderRadius: 12 }}>
          <div style={{ fontSize: 40 }}>🔍</div>
          <div style={{ marginTop: 10 }}>No candidates found. Try a different search.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 4 }}>{candidates.length} candidate{candidates.length !== 1 ? "s" : ""}</div>
          {candidates.map(c => {
            const recColors = c.recommendation ? (REC_COLORS[c.recommendation] || { bg: "#f1f5f9", fg: "#475569" }) : null;
            const statusColors = c.best_status ? (STATUS_COLORS[c.best_status] || { bg: "#f1f5f9", fg: "#475569" }) : null;
            const isExpanded = expanded === c.candidate_id;

            return (
              <div key={c.candidate_id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                  {/* Left: info */}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{c.name}</span>
                      {recColors && <Chip label={c.recommendation} colors={recColors} />}
                      {c.score != null && (
                        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                          Score: <strong style={{ color: NAVY }}>{Math.round(c.score)}</strong>/100
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: "#475569" }}>
                      {[c.current_role, c.location, c.experience_years != null ? `${c.experience_years}y exp` : null].filter(Boolean).join(" · ")}
                    </div>
                    {c.email && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{c.email}</div>}

                    {/* Applied jobs */}
                    {c.applied_jobs?.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                        {c.applied_jobs.slice(0, 3).map((j: any, i: number) => (
                          <span key={i} style={{ background: "#f1f5f9", color: "#475569", fontSize: 11, padding: "2px 8px", borderRadius: 8, fontWeight: 500 }}>
                            {j.title}
                            {statusColors && j.status === c.best_status && (
                              <span style={{ marginLeft: 4, color: statusColors.fg }}>· {String(j.status).replace("_", " ")}</span>
                            )}
                          </span>
                        ))}
                        {c.applied_jobs.length > 3 && (
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>+{c.applied_jobs.length - 3} more</span>
                        )}
                      </div>
                    )}

                    {/* Strengths preview */}
                    {isExpanded && c.strengths?.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Strengths</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {c.strengths.map((s: string, i: number) => (
                            <span key={i} style={{ background: "#f0fdf4", color: "#15803d", fontSize: 11, padding: "2px 8px", borderRadius: 8 }}>{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {isExpanded && c.ai_summary && (
                      <div style={{ marginTop: 10, fontSize: 13, color: "#475569", lineHeight: 1.6, background: "#f8fafc", borderRadius: 8, padding: "10px 12px" }}>
                        {c.ai_summary}
                      </div>
                    )}
                  </div>

                  {/* Right: actions */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: isMobile ? "flex-start" : "flex-end", width: isMobile ? "100%" : "auto" }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
                      {c.has_formatted_cv && (
                        <AsyncButton onClick={() => handleDownloadCV(c)}
                          style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "6px 12px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                          ⬇ CV
                        </AsyncButton>
                      )}
                      <AsyncButton onClick={() => openOutreach(c)}
                        style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "6px 12px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                        ✉ Outreach
                      </AsyncButton>
                      <button onClick={() => { setAddModal({ candidate: c }); setSelectedJob(""); }}
                        style={{ background: NAVY, color: "white", border: "none", padding: "6px 12px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                        {adding[c.candidate_id] ? "Adding…" : "+ Add to Job"}
                      </button>
                      <button onClick={() => { setScoreModal({ candidate: c }); setScoreJob(""); }}
                        style={{ background: "white", color: TEAL, border: `1.5px solid ${TEAL}`, padding: "6px 12px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                        {scoring[c.candidate_id] ? "Scoring…" : "⚡ Score vs Job"}
                      </button>
                      {canDelete && (
                        <button onClick={() => handleDelete(c)} disabled={deleting[c.candidate_id]}
                          title="Delete candidate"
                          style={{ background: "white", color: "#dc2626", border: "1.5px solid #fca5a5", padding: "6px 10px", borderRadius: 7, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: deleting[c.candidate_id] ? 0.5 : 1 }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <button onClick={() => setExpanded(isExpanded ? null : c.candidate_id)}
                      style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 12, cursor: "pointer", padding: 0 }}>
                      {isExpanded ? "Hide details ▲" : "Show details ▼"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add to Job modal ── */}
      {addModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 6px", color: NAVY }}>Add to Job Pipeline</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
              {addModal.candidate.name} will be added at "Reviewed" stage — no AI re-scoring.
            </p>
            <select value={selectedJob} onChange={e => setSelectedJob(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 18 }}>
              <option value="">— select a job —</option>
              {activeJobs.map((j: any) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setAddModal(null)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={!selectedJob || adding[addModal.candidate.candidate_id]} onClick={handleAddToJob}
                style={{ background: NAVY, color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Add to Pipeline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Score vs Job modal ── */}
      {scoreModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 6px", color: NAVY }}>Score vs Job</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 4px" }}>
              {scoreModal.candidate.name}'s CV will be re-scored against the selected job's JD.
            </p>
            <p style={{ fontSize: 12, color: "#f59e0b", margin: "0 0 16px" }}>
              ⚡ Uses 1 AI quota unit
            </p>
            <select value={scoreJob} onChange={e => setScoreJob(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 18 }}>
              <option value="">— select a job —</option>
              {jobs.map((j: any) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setScoreModal(null)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={!scoreJob || scoring[scoreModal.candidate.candidate_id]} onClick={handleScore}
                style={{ background: TEAL, color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {scoring[scoreModal.candidate.candidate_id] ? "Starting…" : "Score Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Outreach modal ── */}
      {outreachCandidate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 540, width: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 14px", color: NAVY }}>Outreach — {outreachCandidate.name}</h3>
            {generatingOutreach ? (
              <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Generating email draft…</div>
            ) : outreachDraft ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4 }}>Subject</label>
                  <input value={outreachDraft.subject} onChange={e => setOutreachDraft((d: any) => ({ ...d, subject: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 7, border: "1.5px solid #e2e8f0", fontSize: 13, boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4 }}>Message</label>
                  <textarea value={outreachDraft.body} onChange={e => setOutreachDraft((d: any) => ({ ...d, body: e.target.value }))}
                    rows={10} style={{ width: "100%", padding: "8px 12px", borderRadius: 7, border: "1.5px solid #e2e8f0", fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => { setOutreachCandidate(null); setOutreachDraft(null); }}
                    style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                  <button disabled={outreachSending} onClick={handleSendOutreach}
                    style={{ background: NAVY, color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                    {outreachSending ? "Sending…" : "Send via Gmail"}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: "#ef4444", fontSize: 13 }}>Failed to generate draft. Close and try again.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
